// Scan runs of a reusable invite (hezo-ops SCAN_MODEL_FLOW § Phase 3).
//
// An invite asks for N runs per pair, done back to back in one visit. Every run
// is its own POST /start with a fresh scanID, and it counts for each pair it
// carries. The server derives the count (`runsDone` per pair in the GET); this
// module only keeps the visit's local view of it, so the app knows which pairs
// the next run is for and which number it has. The GET stays the authority:
// `syncRunPlan` folds a fresh one in, `recordRun` is the fallback when that GET
// fails.

import type { ScanPair } from "./api";

export interface RunPlan {
  /** The pairs the customer picked for this round, in picker order. A
   *  pairless plan holds the one key `INVITE_RUN_KEY`. */
  pairIds: string[];
  /** Runs every pair needs (the invite's snapshot, the same for all pairs). */
  required: number;
  /** Runs each picked pair has so far. */
  done: Record<string, number>;
  /** The invite covers no pair (walk-in, campaign, complaint): its runs count
   *  per invite, and `/start` goes out with empty `pairIds`. */
  pairless?: boolean;
}

/** The one plan key of a pairless invite. Never sent to the server. */
export const INVITE_RUN_KEY = "invite";

/** Run number and total, for "Durchlauf k von N". */
export interface RunInfo {
  index: number;
  total: number;
}

/**
 * Plan the runs for the picked pairs. Null when the invite carries no run
 * counts (minted before the feature) or asks for a single run: the app then
 * behaves exactly as before, one scan and the done screen.
 */
export function planRuns(picked: ScanPair[]): RunPlan | null {
  const required = Math.max(0, ...picked.map((p) => p.runsRequired ?? 0));
  if (required <= 1) return null;
  return {
    pairIds: picked.map((p) => p.id),
    required,
    done: Object.fromEntries(picked.map((p) => [p.id, p.runsDone ?? 0])),
  };
}

/**
 * Plan the runs of a pairless invite (hezo-ops PR C: walk-in, campaign,
 * complaint, API). The GET carries the count on the invite only. Null for a
 * single run, the same rule as `planRuns`.
 */
export function planInviteRuns(counts: {
  runsDone?: number;
  runsRequired?: number;
}): RunPlan | null {
  const required = counts.runsRequired ?? 0;
  if (required <= 1) return null;
  return {
    pairIds: [INVITE_RUN_KEY],
    required,
    done: { [INVITE_RUN_KEY]: counts.runsDone ?? 0 },
    pairless: true,
  };
}

/** The `pairIds` a `/start` for these plan keys sends: none when pairless. */
export function startPairIds(plan: RunPlan | null, keys: string[]): string[] {
  return plan?.pairless ? [] : keys;
}

/** Take the invite-level count from a fresh GET (pairless plan). */
export function syncInviteRunPlan(
  plan: RunPlan,
  counts: { runsDone?: number; done: boolean },
): RunPlan {
  const done = counts.done
    ? plan.required
    : (counts.runsDone ?? plan.done[INVITE_RUN_KEY] ?? 0);
  return { ...plan, done: { [INVITE_RUN_KEY]: done } };
}

/**
 * Pairs whose runs have started but not finished. A reopened link continues
 * exactly these, without the picker: the runs of one visit share their
 * selection, and hezo-ops refuses a `/start` that drops or adds a pair
 * (409 `resume_required`). Empty when nothing is under way.
 */
export function startedRunPairs(pairs: ScanPair[]): ScanPair[] {
  return pairs.filter((p) => {
    const done = p.runsDone ?? 0;
    return done > 0 && done < (p.runsRequired ?? 0);
  });
}

/** Picked pairs that still need a run. */
export function openRunPairs(plan: RunPlan): string[] {
  return plan.pairIds.filter((id) => (plan.done[id] ?? 0) < plan.required);
}

/** Number of the next run for these pairs: one past the least advanced. */
export function runInfo(plan: RunPlan, pairIds: string[]): RunInfo {
  const least = Math.min(...pairIds.map((id) => plan.done[id] ?? 0));
  return { index: Math.min(least + 1, plan.required), total: plan.required };
}

/** Count one finished run for `pairIds` locally (the GET was not reachable). */
export function recordRun(plan: RunPlan, pairIds: string[]): RunPlan {
  const done = { ...plan.done };
  for (const id of pairIds) done[id] = (done[id] ?? 0) + 1;
  return { ...plan, done };
}

/**
 * Take the counts from a fresh GET. A picked pair missing from `open` has all
 * its runs (or the operator already bound it), so it is complete.
 */
export function syncRunPlan(plan: RunPlan, open: ScanPair[]): RunPlan {
  const byId = new Map(open.map((p) => [p.id, p]));
  const done: Record<string, number> = {};
  for (const id of plan.pairIds) {
    const p = byId.get(id);
    done[id] = p ? (p.runsDone ?? plan.done[id] ?? 0) : plan.required;
  }
  return { ...plan, done };
}
